import { NetsuiteCreditMemoSources } from "@redotech/redo-model/integrations/returns/netsuite/netsuite";
import { CompensationMethod } from "@redotech/redo-model/return";
import { NetSuiteTransactionTypeZodSchema } from "@redotech/redo-model/return-integration-types";
import * as z from "zod/v4";
import { array, boolean, object, string, enum as zEnum } from "zod/v4";
import {
  netsuiteCreditMemoCustomizationSchema,
  netsuiteProcessingCustomFieldSchema,
  netsuiteRMACustomizationSchema,
} from "./shared-schemas";

const returnReasonSchema = object({ reason: string(), refName: string() });

const netsuiteReturnSchema = object({
  location: string().optional(),
  reasonDefault: string().optional(),
  reasons: array(returnReasonSchema).optional(),
});

const netsuiteItemReceiptSchema = object({
  createItemReceipt: boolean().optional(),
  processReturnsEnabled: boolean().optional(),
  customFormId: string().optional(),
  processingCustomFields: array(netsuiteProcessingCustomFieldSchema).optional(),
});

const netsuiteCreditMemoSchema = object({
  createCreditMemo: boolean().optional(),
  customFormId: string().optional(),
  sources: array(zEnum(NetsuiteCreditMemoSources)).optional(),
  creditMemoCustomizations: netsuiteCreditMemoCustomizationSchema.optional(),
});

const returnTypeEnum = zEnum(["return", "claim", "warranty", "recycle"]);

const netsuiteReturnAuthorizationSchema_ = object({
  createReturnAuthorization: boolean().optional(),
  closeReturnAuthorization: object({
    enabled: boolean().optional(),
    returnTypes: array(returnTypeEnum).optional(),
    permissionList: array(zEnum(["close", "automation"])).optional(),
  }).optional(),
  includeGreenItems: boolean().optional(),
  productReturnReasonField: string().optional(),
  allowFullGreenReturns: boolean().optional(),
  allowInStoreReturns: boolean().optional(),
  autoApproveReturnAuthorization: boolean().optional(),
  shipmentSettings: object({
    shouldUpdateShipment: boolean().optional(),
    shipmentStatusField: string().optional(),
    shipmentDateField: string().optional(),
    shipmentTrackerNumberField: string().optional(),
  }).optional(),
});

const netsuiteVendorReturnAuthorizationSchema = object({
  enabled: boolean().optional(),
  closeVendorReturnAuthorization: object({
    enabled: boolean().optional(),
    returnTypes: array(returnTypeEnum).optional(),
  }).optional(),
});
const netsuiteReturnAuthorizationSchema: z.ZodType<NetsuiteReturnAuthorization> =
  netsuiteReturnAuthorizationSchema_;
export interface NetsuiteReturnAuthorization
  extends z.infer<typeof netsuiteReturnAuthorizationSchema_> {}

const netsuiteCustomerRefundSchema = object({
  accountId: string().optional(),
  createCustomerRefund: boolean().optional(),
  customFormId: string().optional(),
  orderIdField: string().optional(),
  allowedCompensationMethods: array(zEnum(CompensationMethod)).optional(),
});

const netsuiteSupportCaseSchema = object({
  createSupportCasesForWarranties: boolean().optional(),
});

const orderNameModificationSchema = object({
  match: string(),
  replace: string(),
});

export const getNetSuiteIntegration = {
  name: "getNetSuiteIntegration" as const,
  input: object({}),
  output: object({
    netsuiteIntegration: object({
      accountId: string(),
      authMethod: zEnum(["tba", "oauth2"]).optional(),
      creditMemo: netsuiteCreditMemoSchema.optional(),
      returnAuthorization: netsuiteReturnAuthorizationSchema.optional(),
      itemReceipt: netsuiteItemReceiptSchema.optional(),
      customerRefund: netsuiteCustomerRefundSchema.optional(),
      clientId: string().optional(),
      clientSecret: string().optional(),
      token: string().optional(),
      tokenSecret: string().optional(),
      createExchangeSalesOrders: boolean().optional(),
      exchangedItemField: string().optional(),
      itemReceiptSkuField: string().optional(),
      itemValue: zEnum([
        "credit",
        "refund",
        "total",
        "total-pre-discount",
      ]).optional(),
      discountInventoryItemId: string().optional(),
      orderNameModification: orderNameModificationSchema.optional(),
      redoReturnIdField: string().optional(),
      returns: netsuiteReturnSchema.optional(),
      warranties: boolean().optional(),
      supportCase: netsuiteSupportCaseSchema.optional(),
      vendorReturnAuthorization:
        netsuiteVendorReturnAuthorizationSchema.optional(),
      salesTaxItemId: string().optional(),
      shopifyOrderNameField: string().optional(),
      skuField: string().optional(),
      taxMode: zEnum(["celigo", "override"]),
      trackingCodeField: string().optional(),
      transactionType: NetSuiteTransactionTypeZodSchema,
      RMACustomization: netsuiteRMACustomizationSchema.optional(),
    }).optional(),
  }),
};
